import React, { useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Grid,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from "@chakra-ui/react";
import CopyButton from "components/common/CopyButton";
import JinjaHighlightedTextarea from "components/common/JinjaHighlightedTextarea";
import { modelOptions } from "utils/helperFuncUtils";
import { FiTrash2, FiSettings, FiLock, FiCopy } from "react-icons/fi";
import { DarwinAgent, PromptProto } from "build-ts/darwin_agent";
import { DarwinHeader } from "build-ts/common";
import { reasoningEffortLevelToJSON, ReasoningEffortLevel, reasoningEffortLevelFromJSON } from "build-ts/common";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FaBookOpen, FaMagic, FaRegCheckCircle } from "react-icons/fa";
import { LuScrollText } from "react-icons/lu";
import { PiBracketsCurlyBold } from "react-icons/pi";

interface EvaAgentRightPanelProps {
  agent: DarwinAgent;
  onPromptEditorChange: (value: string) => void;
  onPromptConfigChange?: (field: string, value: any) => void;
  isCreateMode?: boolean;
  defaultTimeZone: string;
  temperatureError: string;
  isPromptEditing: boolean;
  assignedPrompts: DarwinHeader[];
  selectedPromptId: string;
  currentPrompt: PromptProto | undefined;
  isLoadingPrompts: boolean;
  isUpdating: boolean;
  // Event handlers
  handlePromptSelection: (promptId: string) => void;
  handleUpdatePrompt: () => void;
  handleOpenValidateModal: () => void;
  handleGeneratePrompt: () => void;
  setIsPromptEditing: (value: boolean) => void;
  setCurrentPrompt: React.Dispatch<React.SetStateAction<PromptProto | undefined>>;
  setTemperatureError: (value: string) => void;
  setShowDeleteConfirmation: (value: boolean) => void;
  setShowDuplicateConfirmation: (value: boolean) => void;
  onViewDocs?: () => void;
}

const EvaAgentRightPanel: React.FC<EvaAgentRightPanelProps> = React.memo(({
  agent,
  onPromptEditorChange,
  onPromptConfigChange,
  isCreateMode = false,
  defaultTimeZone,
  temperatureError,
  isPromptEditing,
  assignedPrompts,
  selectedPromptId,
  currentPrompt,
  isLoadingPrompts,
  isUpdating,
  // Event handlers
  handlePromptSelection,
  handleUpdatePrompt,
  handleOpenValidateModal,
  handleGeneratePrompt,
  setIsPromptEditing,
  setCurrentPrompt,
  setTemperatureError,
  setShowDeleteConfirmation,
  setShowDuplicateConfirmation,
  onViewDocs,
}) => {
  // Calculate temperature disable conditions
  const isTemperatureDisabled = useMemo(() => {
    const is5_2Model = currentPrompt?.model_config?.model_name === "gpt-5.2";
    // TODO: reasoning_effort_level is set as string in the Input/Select HTML but it is typed as ReasoningEffortLevel | undefined
    // fix the set value in ReasoningEffort FormControl
    const isReasoningNotNone = String(currentPrompt?.model_config?.reasoning_effort_level) !== reasoningEffortLevelToJSON(ReasoningEffortLevel.NONE);
    const isTemperatureDisabledFor5_2 = is5_2Model && isReasoningNotNone;
    return (!isPromptEditing && !isCreateMode) || isTemperatureDisabledFor5_2;
  }, [isPromptEditing, isCreateMode, currentPrompt]);

  // Early return if currentPrompt is undefined and not in create mode
  if (!currentPrompt && !isCreateMode) {
    return (
      <Box
        width="100%"
        minWidth={0}
        height="100%"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
        }}
      >
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
          height="max-content"
        >
          <Box p={6} display="flex" gap={4} alignItems="stretch">
            <Text>No prompt selected. Please select a prompt from the dropdown.</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      minWidth={0}
      height="100%"
      overflowY="auto"
      css={{
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
      }}
    >
      <Grid templateColumns="minmax(0, 1fr) minmax(0, 2fr)" gap={3} h="100%" width="100%">
            {/* Left Column - Controls (1/3 of right panel = 25% of screen) */}
            <VStack spacing={3} align="stretch" h="100%"
             bg="white"
             px={4}
             py={4}
             borderRadius="lg"
             boxShadow="lg">

              {/* Prompt Section */}
              {!isCreateMode && (
                <Box
                >
                  <HStack justify="space-between" align="center">
                  <HStack mb={3}>
                    <Icon as={LuScrollText} boxSize={5} color="purple.700" />
                    <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
                      Prompt
                    </Text>
                  </HStack>
                   {!isPromptEditing ? (
                     <Button
                       size="sm"
                       colorScheme="purple"
                       onClick={() => setIsPromptEditing(true)}
                       isDisabled={!currentPrompt}
                       leftIcon={<Icon as={FiLock} />}
                       variant="solid"
                       bg="purple.700"
                       color="white"
                       _hover={{ bg: "purple.600" }}
                     >
                       Edit
                     </Button>
                   ) : (
                     <HStack spacing={2}>
                       <Button
                         size="sm"
                         variant="outline"
                         onClick={() => setIsPromptEditing(false)}
                       >
                         Cancel
                       </Button>
                       <Button
                         size="sm"
                         colorScheme="green"
                         onClick={() => handleUpdatePrompt()}
                         isLoading={isUpdating}
                       >
                         Save
                       </Button>
                     </HStack>
                   )}
                   </HStack>
                  <VStack spacing={3} align="stretch" w="100%">
                    <FormControl>
                      <FormLabel fontSize="sm" mb={2} fontWeight="600">Prompt Version</FormLabel>
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        size="sm"
                        bg="green.50"
                        fontSize="sm"
                        fontWeight="700"
                        isDisabled={isLoadingPrompts || isUpdating}
                        width="100%"
                        textAlign="left"
                        _hover={{ bg: "green.100" }}
                        _active={{ bg: "green.200" }}
                        px={3}
                        py={2}
                        color="black"
                        borderWidth="1px"
                        borderColor="green.600"
                      >
                        {isLoadingPrompts
                          ? "Loading prompts..."
                          : assignedPrompts.find((p) => String(p.id) === String(selectedPromptId))
                            ? (() => {
                                const prompt = assignedPrompts.find((p) => String(p.id) === String(selectedPromptId));
                                const version = prompt?.version || 1;
                                const modifiedDate = prompt?.last_modified_date || 0;
                                const isActive = prompt?.is_active || false;
                                return `v${version}_${epochConverterWithTimeZone(modifiedDate, "dd-MMM-yyyy", defaultTimeZone)}${isActive ? " 🟢" : ""}`;
                              })()
                            : "Select a prompt"
                        }
                      </MenuButton>
                      <MenuList maxH="250px" overflowY="auto" px={0}>
                        {assignedPrompts.map((prompt, index) => {
                          const promptId = prompt.id || "";
                          const isActive = prompt.is_active || false;
                          const modifiedDate = prompt.last_modified_date || 0;
                          const version = prompt.version || 1;
                          return (
                            <MenuItem
                              key={promptId || `prompt-${index}`}
                              value={String(promptId)}
                              onClick={() => handlePromptSelection(promptId)}
                              fontWeight="600"
                              bg={selectedPromptId === String(promptId) ? "green.100" : undefined}
                            >
                              {`v${version}_${epochConverterWithTimeZone(modifiedDate || 0, "dd-MMM-yyyy", defaultTimeZone)}${isActive ? " 🟢 " : ""}`}
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Menu>
                  </FormControl>
                    {/* Active/Activate Toggle */}
                    {(assignedPrompts.filter(
                      (p: DarwinHeader) => p.is_active
                    )[0]?.id === currentPrompt?.header?.id) ? (
                      <Box
                        borderColor="green.500"
                        borderWidth="1px"
                        borderRadius="md"
                        px={3}
                        py={1}
                        bg="green.600"
                        textAlign="center"
                      >
                        <HStack spacing={2} alignItems="center" justifyContent="center">
                            <Icon as={FaRegCheckCircle} boxSize={4} color="white" />
                            <Text fontSize="sm" color="white" fontWeight="700" mb={0}>Version active</Text>
                          </HStack>
                      </Box>
                    ) : (
                      <HStack
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                        px={2}
                        py={0}
                        borderWidth="1px"
                        borderColor="green.600"
                        bg="green.50"
                        borderRadius="md"
                      >
                        <Text fontSize="sm" color="gray.700" fontWeight="700" mb={0}>
                          Activate version
                        </Text>
                        <Box pt={2}> {/* Slight padding top adjustment for switch alignment */}
                          <Switch
                            isChecked={currentPrompt?.header?.is_active || false}
                            onChange={(e) => {
                              const isActive = e.target.checked;
                              setCurrentPrompt(prev => {
                                if (!prev) return prev;
                                return {
                                  ...prev,
                                  header: {
                                    ...prev.header,
                                    is_active: isActive
                                  }
                                };
                              });
                              if (onPromptConfigChange) {
                                onPromptConfigChange("header.is_active", isActive);
                              }
                            }}
                            colorScheme="blue"
                            isDisabled={!isPromptEditing && !isCreateMode}
                            size="md"
                          />
                        </Box>
                      </HStack>
                    )}
                    <HStack
                      spacing={2}
                      justify="flex-start"
                      flexWrap="wrap"
                      w="100%"
                      alignItems="stretch"
                    >
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="outline"
                        onClick={() => setShowDuplicateConfirmation(true)}
                        isDisabled={!currentPrompt}
                        leftIcon={<FiCopy />}
                        w="50%"
                        minW={0}
                        flex="1 1 0"
                        fontWeight="700"
                      >
                        Duplicate
                      </Button>
                      <Button
                        aria-label="Delete prompt"
                        leftIcon={<FiTrash2 />}
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => setShowDeleteConfirmation(true)}
                        isDisabled={!currentPrompt}
                        w="50%"
                        minW={0}
                        flex="1 1 0"
                        fontWeight="700"
                      >
                        Delete
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* Prompt Description Section */}
              <Box>
                <FormControl>
                  <FormLabel fontSize="sm" mb={2} fontWeight="600">Changelog</FormLabel>
                  <Textarea
                    name="prompt description"
                    value={currentPrompt?.header?.description || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCurrentPrompt(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          header: {
                            ...prev.header,
                            description: value
                          }
                        };
                      });
                      if (onPromptConfigChange) {
                        onPromptConfigChange("header.description", value);
                      }
                    }}
                    placeholder="Prompt description"
                    rows={18}
                    isDisabled={!isPromptEditing && !isCreateMode}
                    borderColor="gray.200"
                    fontSize="sm"
                    resize="none"
                  />
                </FormControl>
                {/* Footer - Timestamps */}
                {!isCreateMode && (
                  <VStack align="flex-start" spacing={1} mt={2}>
                    <Text fontSize="xs" color="gray.600" mb={0}>
                     Last Modified: {currentPrompt?.header?.last_modified_date ? epochConverterWithTimeZone(currentPrompt.header?.last_modified_date, "dd-MMM-yyyy hh:mm a", defaultTimeZone) : ""} {defaultTimeZone.toUpperCase()}
                    </Text>
                  </VStack>
                )}
              </Box>

              <Divider my={0} />
              {/* Model Parameters Section */}
              <Box>
                <HStack mb={3}>
                  <Icon as={FiSettings} boxSize={5} color="purple.700" />
                  <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
                    Model Parameters
                  </Text>
                </HStack>

                <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" mb={2} fontWeight="600">Model</FormLabel>
                <Select
                  value={`${currentPrompt?.model_config?.model_provider}:${currentPrompt?.model_config?.model_name}`}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const [provider, modelName] = selectedValue.split(":");
                    // get the first valid reasoning effort level for the selected model
                    const reasoningOptions = modelOptions[provider as keyof typeof modelOptions]?.find(m => m.model_name === modelName && (m.model_options as any)?.reasoning?.length > 0);
                    const defaultReasoningLevel = reasoningOptions
                      ? reasoningEffortLevelFromJSON((reasoningOptions.model_options as any)?.reasoning?.[0])
                      : ReasoningEffortLevel.REASONING_NOT_VALID;
                    // Update form state for Darwin proto
                    setCurrentPrompt(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        model_config: {
                          ...prev.model_config,
                          model_name: modelName,
                          model_provider: provider,
                          reasoning_effort_level: defaultReasoningLevel,
                        } as any
                      };
                    });

                    // Update prompt config for parent component
                    if (onPromptConfigChange) {
                      onPromptConfigChange("model_config.model_name", modelName);
                      onPromptConfigChange("model_config.model_provider", provider);
                      onPromptConfigChange("model_config.reasoning_effort_level", defaultReasoningLevel);
                    }
                  }}
                  isDisabled={!isPromptEditing && !isCreateMode}
                  size="sm"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                >
                  {Object.entries(modelOptions).map(([provider, models]) => (
                    <optgroup key={provider} label={provider.replace("_", " ").toUpperCase()}>
                      {models.map(({model_name, model_options}) => (
                        <option key={`${provider}:${model_name}`} value={`${provider}:${model_name}`}>
                          {model_name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </FormControl>
                {/* Temperature and Reasoning in one row */}
                <HStack spacing={3} align="flex-start">
                  <FormControl isInvalid={!!temperatureError} flex="1">
                    <FormLabel fontSize="sm" mb={2} fontWeight="600">Temperature</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      step="0.1"
                      value={currentPrompt?.model_config?.temperature?.toString() || ""}
                      onChange={(e) => {
                        const inputValue = e.target.value;

                        if (inputValue === "") {
                          setTemperatureError("");
                          setCurrentPrompt(prev => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              model_config: {
                                ...prev.model_config,
                                temperature: 0
                              }
                            };
                          });
                          if (onPromptConfigChange) {
                            onPromptConfigChange("model_config.temperature", 0);
                          }
                          return;
                        }

                        const numValue = parseFloat(inputValue);
                        if (isNaN(numValue)) {
                          setTemperatureError("Please enter a valid number");
                          return;
                        }

                        // Validate range before updating
                        if (numValue < 0 || numValue > 2) {
                          setTemperatureError("Temperature must be between 0 and 2");
                          return;
                        }

                        // Only update if valid
                        setTemperatureError("");
                        setCurrentPrompt(prev => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            model_config: {
                              ...prev.model_config,
                              temperature: numValue
                            }
                          };
                        });
                        if (onPromptConfigChange) {
                          onPromptConfigChange("model_config.temperature", numValue);
                        }
                      }}
                      onWheel={(e) => {
                        if (isTemperatureDisabled) return; // Don't allow wheel when disabled

                        e.preventDefault();
                        e.stopPropagation();

                        const currentValue = currentPrompt?.model_config?.temperature ?? 0;
                        const delta = e.deltaY > 0 ? -0.1 : 0.1;
                        const newValue = Math.round((currentValue + delta) * 10) / 10;

                        // Clamp the value between 0 and 2
                        const clampedValue = Math.max(0, Math.min(2, newValue));

                        if (clampedValue !== currentValue) {
                          setTemperatureError("");
                          setCurrentPrompt(prev => {
                            if (prev) {
                              return {
                                ...(prev),
                                model_config: {
                                  ...(prev).model_config,
                                  temperature: clampedValue
                                }
                              };
                            }
                            return prev;
                          });
                          if (onPromptConfigChange) {
                            onPromptConfigChange("model_config.temperature", clampedValue);
                          }
                        }
                      }}
                      placeholder="0"
                      bg="white"
                      borderColor="gray.300"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                      }}
                      fontWeight="500"
                      isDisabled={isTemperatureDisabled}
                      color={isTemperatureDisabled ? "gray.400" : "black"}
                    />
                    {temperatureError && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        {temperatureError}
                      </Text>
                    )}
                  </FormControl>

                  {/* Reasoning Effort - Only show for specific models */}
                  {modelOptions[(currentPrompt?.model_config?.model_provider || "") as keyof typeof modelOptions]?.some(m => m.model_name === currentPrompt?.model_config?.model_name && (m.model_options as any)?.reasoning?.length > 0) && (
                    <FormControl flex="1">
                      <FormLabel fontSize="sm" mb={2} fontWeight="600">Reasoning Effort</FormLabel>
                      <Select
                        value={reasoningEffortLevelToJSON(reasoningEffortLevelFromJSON((currentPrompt?.model_config as any)?.reasoning_effort_level) ?? ReasoningEffortLevel.REASONING_NOT_VALID)}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCurrentPrompt(prev => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              model_config: {
                                ...prev.model_config,
                                reasoning_effort_level: reasoningEffortLevelFromJSON(value)
                              } as any
                            };
                          });
                          if (onPromptConfigChange) {
                            onPromptConfigChange("model_config.reasoning_effort_level", reasoningEffortLevelFromJSON(value));
                          }
                        }}
                        isDisabled={!isPromptEditing && !isCreateMode}
                        size="sm"
                        bg="white"
                        borderColor="gray.300"
                        _hover={{ borderColor: "blue.400" }}
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                      >
                        {((modelOptions[currentPrompt?.model_config?.model_provider as keyof typeof modelOptions] || [])?.find((m: any) => m.model_name === currentPrompt?.model_config?.model_name)?.model_options as any)?.reasoning?.map((level: ReasoningEffortLevel) => (
                            <option key={reasoningEffortLevelToJSON(level)} value={reasoningEffortLevelToJSON(level)}>
                              {reasoningEffortLevelToJSON(level).replace(/_/g, " ").toUpperCase()}
                            </option>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                </HStack>
                </VStack>
              </Box>
            </VStack>

            {/* Right Column - System Prompt Editor (2/3 of right panel = 50% of screen) */}
            <VStack
              spacing={1}
              align="stretch"
              h="100%"
              bg="white"
              px={4}
              py={4}
              borderRadius="lg"
              boxShadow="lg"
             >
                {/* System Prompt Header */}
                <HStack justify="space-between" align="center" mb={2}>
                  <HStack mb={0}>
                    <Icon as={PiBracketsCurlyBold} boxSize={5} color="purple.700" />
                    <Text fontSize="lg" color="purple.700" fontWeight="700" mb={0}>
                      Prompt Editor
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                  <Button
                    leftIcon={<FaBookOpen />}
                    size="sm"
                    variant="solid"
                    onClick={onViewDocs}
                    colorScheme="purple"
                    bg="purple.700"
                    color="white"
                    _hover={{ borderColor: "purple.600", bg: "purple.600" }}
                    fontWeight="700"
                  >
                    Jinja Docs
                  </Button>
                    <Button
                    leftIcon={<FaMagic />}
                      variant="solid"
                      size="sm"
                      onClick={handleGeneratePrompt}
                      colorScheme="purple"
                      bg="blue.600"
                      color="white"
                      _hover={{ borderColor: "purple.600", bg: "purple.600" }}
                      fontWeight="700"
                    >
                      Generate
                    </Button>
                    <CopyButton value={currentPrompt?.template}/>
                  </HStack>
                </HStack>
                {/* Prompt Editor */}
                <JinjaHighlightedTextarea
                  value={currentPrompt?.template || ""}
                  onChange={(value) => {
                    onPromptEditorChange(value);
                    setCurrentPrompt(prev => ({ ...prev, template: value }));
                    if (onPromptConfigChange) {
                      onPromptConfigChange("template", value);
                    }
                  }}
                  placeholder="Enter your system prompt here. Use Jinja template syntax for variables like {{agent_name}}, {{plan_name}}, {{MKG_INFO}}, etc."
                  isReadOnly={!isPromptEditing && !isCreateMode}
                />
            </VStack>
          </Grid>
    </Box>
  );
});

export default EvaAgentRightPanel;
